import { Controller } from "@hotwired/stimulus"

// Manages the "Save as Alert" panel — creates a Logs::Alert for the current query.
export default class extends Controller {
  static targets = [
    "panel", "trigger", "name", "interval", "threshold", "timeRange", "status", "list", "firingsPanel", "firingsList",
    "newForm", "nameInput", "queryEditor", "timeRangeInput", "intervalInput", "thresholdInput", "formStatus",
  ]
  static values = { url: String, firingsBaseUrl: String, createUrl: String }

  #boundOutside = null
  #open = false

  connect() {
    this.#boundOutside = e => {
      if (this.#open && !this.element.contains(e.target)) this.#close()
    }
    document.addEventListener("click", this.#boundOutside)
    this.#loadAlerts()
  }

  disconnect() {
    document.removeEventListener("click", this.#boundOutside)
  }

  toggle() {
    this.#open ? this.#close() : this.#openPanel()
  }

  async save(event) {
    event.preventDefault()
    const name = this.nameTarget.value.trim()
    if (!name) { this.nameTarget.focus(); return }

    const query = document.querySelector("textarea[name='query']")?.value || ""
    const body = new URLSearchParams({
      "alert[name]": name,
      "alert[query]": query,
      "alert[check_interval_minutes]": this.intervalTarget.value,
      "alert[threshold]": this.thresholdTarget.value,
      "alert[time_range_preset]": this.hasTimeRangeTarget ? this.timeRangeTarget.value : "last_1h",
      authenticity_token: document.querySelector('meta[name="csrf-token"]')?.content || "",
    })

    this.statusTarget.textContent = "Saving..."
    try {
      const resp = await fetch(this.urlValue, {
        method: "POST",
        body,
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "",
        },
      })
      if (resp.ok) {
        await resp.json()
        this.statusTarget.textContent = "Alert saved!"
        await this.#loadAlerts()
        setTimeout(() => { this.statusTarget.textContent = "" }, 2000)
      } else {
        const data = await resp.json()
        this.statusTarget.textContent = data.errors?.join(", ") || "Save failed"
      }
    } catch {
      this.statusTarget.textContent = "Save failed"
    }
  }

  async showFirings(event) {
    if (!this.hasFiringsPanelTarget) return
    const alertId = event.currentTarget.dataset.alertId
    const alertName = event.currentTarget.dataset.alertName || "Alert"
    this.firingsPanelTarget.classList.remove("hidden")
    this.firingsPanelTarget.querySelector(".siem-alert-firings-title").textContent = alertName
    this.firingsListTarget.innerHTML = '<div class="siem-alert-firings-loading"><i class="fa fa-spin fa-spinner"></i></div>'

    try {
      const url = `${this.firingsBaseUrlValue}/${alertId}/firings`
      const resp = await fetch(url, { headers: { Accept: "application/json" } })
      const firings = await resp.json()
      this.#renderFirings(firings)
    } catch {
      this.firingsListTarget.innerHTML = '<div class="siem-alert-firings-empty">Failed to load</div>'
    }
  }

  hideFirings() {
    this.firingsPanelTarget?.classList.add("hidden")
  }

  showNewForm() {
    if (!this.hasNewFormTarget) return
    this.newFormTarget.classList.remove("hidden")
    this.nameInputTarget.focus()
    if (this.hasFormStatusTarget) this.formStatusTarget.textContent = ""
  }

  hideNewForm() {
    if (!this.hasNewFormTarget) return
    this.newFormTarget.classList.add("hidden")
    if (this.hasFormStatusTarget) this.formStatusTarget.textContent = ""
  }

  async createAlert(event) {
    event.preventDefault()
    const name = this.nameInputTarget.value.trim()
    if (!name) { this.nameInputTarget.focus(); return }

    const query = this.queryEditorTarget?.querySelector("textarea")?.value || ""
    const body = new URLSearchParams({
      "alert[name]": name,
      "alert[query]": query,
      "alert[check_interval_minutes]": this.intervalInputTarget.value,
      "alert[threshold]": this.thresholdInputTarget.value,
      "alert[time_range_preset]": this.timeRangeInputTarget.value,
      authenticity_token: document.querySelector('meta[name="csrf-token"]')?.content || "",
    })

    if (this.hasFormStatusTarget) this.formStatusTarget.textContent = "Saving…"
    try {
      const url = this.hasCreateUrlValue ? this.createUrlValue : this.urlValue
      const resp = await fetch(url, {
        method: "POST",
        body,
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "",
        },
      })
      if (resp.ok) {
        if (this.hasFormStatusTarget) this.formStatusTarget.textContent = "Rule saved!"
        this.nameInputTarget.value = ""
        await this.#loadAlerts()
        setTimeout(() => { this.hideNewForm() }, 1500)
      } else {
        const data = await resp.json()
        if (this.hasFormStatusTarget) this.formStatusTarget.textContent = data.errors?.join(", ") || "Save failed"
      }
    } catch {
      if (this.hasFormStatusTarget) this.formStatusTarget.textContent = "Save failed"
    }
  }

  async deleteAlert(event) {
    const alertId = event.currentTarget.dataset.alertId
    const csrf = document.querySelector('meta[name="csrf-token"]')?.content || ""
    try {
      await fetch(`${this.urlValue}/${alertId}`, {
        method: "DELETE",
        headers: { "X-CSRF-Token": csrf },
      })
      await this.#loadAlerts()
      this.firingsPanelTarget?.classList.add("hidden")
    } catch { /* silent */ }
  }

  async #openPanel() {
    this.#open = true
    this.panelTarget.classList.remove("hidden")
    this.triggerTarget.classList.add("active")
    const query = document.querySelector("textarea[name='query']")?.value || ""
    const where = query.match(/where\s+(.{1,30})/i)
    this.nameTarget.value = where ? `Alert: ${where[1].trim()}` : "SIEM Alert"
    this.statusTarget.textContent = ""
    this.nameTarget.focus()
    await this.#loadAlerts()
  }

  #close() {
    this.#open = false
    this.panelTarget.classList.add("hidden")
    this.triggerTarget.classList.remove("active")
    this.firingsPanelTarget?.classList.add("hidden")
  }

  async #loadAlerts() {
    if (!this.hasListTarget) return
    try {
      const resp = await fetch(this.urlValue, { headers: { Accept: "application/json" } })
      const alerts = await resp.json()
      this.#renderAlerts(alerts)
    } catch { /* silent */ }
  }

  #renderAlerts(alerts) {
    if (!this.hasListTarget) return
    if (!alerts.length) {
      this.listTarget.innerHTML = '<div class="siem-alert-empty">No alerts saved yet.</div>'
      return
    }
    this.listTarget.innerHTML = alerts.map(a => {
      const lastFired = a.last_triggered_at
        ? new Date(a.last_triggered_at).toLocaleString()
        : "never"
      const firingBadge = a.firing_count > 0
        ? `<span class="siem-alert-firing-badge" title="Total firings">${a.firing_count} firing${a.firing_count !== 1 ? "s" : ""}</span>`
        : '<span class="siem-alert-firing-badge siem-alert-firing-badge--none">0 firings</span>'
      return `
        <div class="siem-alert-item">
          <span class="siem-alert-name" title="${this.#esc(a.name)}">${this.#esc(a.name)}</span>
          <span class="siem-alert-meta">${a.check_interval_minutes}m · ≥${a.threshold} · ${a.time_range_preset || "last_1h"}</span>
          <span class="siem-alert-perf">${firingBadge} · last: ${lastFired}</span>
          <button class="siem-alert-firings-btn" title="View firing history"
                  data-alert-id="${a.id}"
                  data-alert-name="${this.#esc(a.name)}"
                  data-action="click->accounts--logs--alert#showFirings">
            <i class="fa fa-history"></i>
          </button>
          <button class="siem-alert-delete-btn" title="Delete alert"
                  data-alert-id="${a.id}"
                  data-action="click->accounts--logs--alert#deleteAlert">
            <i class="fa fa-trash"></i>
          </button>
        </div>
      `
    }).join("")
  }

  #renderFirings(firings) {
    if (!firings.length) {
      this.firingsListTarget.innerHTML = '<div class="siem-alert-firings-empty">No firings recorded yet.</div>'
      return
    }
    this.firingsListTarget.innerHTML = firings.map(f => {
      const firedAt = new Date(f.fired_at).toLocaleString()
      const summary = f.pythia_summary
        ? `<div class="siem-alert-firing-summary">${this.#esc(f.pythia_summary)}</div>`
        : '<div class="siem-alert-firing-summary siem-alert-firing-summary--pending">Pythia triage in progress…</div>'
      const qp = `time_range=${encodeURIComponent(f.query_start + " - " + f.query_end)}${f.query ? "&query=" + encodeURIComponent(f.query) : ""}`
      return `
        <div class="siem-alert-firing">
          <div class="siem-alert-firing-header">
            <span class="siem-alert-firing-time">${firedAt}</span>
            <span class="siem-alert-firing-count">${f.result_count} results</span>
            <a class="siem-alert-firing-link" href="?${qp}" title="Re-run query for this window">
              <i class="fa fa-search"></i>
            </a>
          </div>
          ${summary}
        </div>
      `
    }).join("")
  }

  #esc(str) {
    return String(str)
      .replace(/&/g, "&amp;").replace(/</g, "&lt;")
      .replace(/>/g, "&gt;").replace(/"/g, "&quot;")
  }
}
